import React, { useState, useEffect } from 'react';
import { useTheme } from '../../context/ThemeContext';
import { FaLock, FaKey, FaCopy, FaCheck, FaSun, FaMoon } from 'react-icons/fa';
import HeaderSection from '../../components/HeaderSection/HeaderSection';
import BottomSection from '../../components/BottomSection/BottomSection';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLock } from "@fortawesome/free-solid-svg-icons";


const PasswordGenerator = () => {
  const [password, setPassword] = useState('');
  const { darkMode } = useTheme();
  const [length, setLength] = useState(12);
  const [uppercase, setUppercase] = useState(true);
  const [lowercase, setLowercase] = useState(true);
  const [numbers, setNumbers] = useState(true);
  const [symbols, setSymbols] = useState(false);
  const [excludeSimilar, setExcludeSimilar] = useState(false);
  const [strength, setStrength] = useState('Medium');
  const [strengthLevel, setStrengthLevel] = useState(2);
  const [copied, setCopied] = useState(false);
   // Set to light mode by default

  const similarChars = /[ilLI|`oO0]/g;

  useEffect(() => {
    generatePassword();
  }, [length, uppercase, lowercase, numbers, symbols, excludeSimilar]);

  const updateLengthValue = (value) => {
    setLength(value);
  };

  const checkStrength = () => {
    let score = 0;
    
    // Length contributes up to 50% of the score
    score += Math.min(length / 32 * 50, 50);
    
    // Character types contribute to the remaining 50%
    const typeCount = [uppercase, lowercase, numbers, symbols].filter(Boolean).length;
    score += (typeCount / 4) * 50;
    
    // Determine strength level
    let newStrengthLevel;
    let newStrength;
    let strengthColor;
    
    if (score >= 75) {
      newStrengthLevel = 4;
      newStrength = 'Strong';
      strengthColor = 'text-green-500';
    } else if (score >= 50) {
      newStrengthLevel = 3;
      newStrength = 'Good';
      strengthColor = 'text-green-400';
    } else if (score >= 25) {
      newStrengthLevel = 2;
      newStrength = 'Medium';
      strengthColor = 'text-yellow-500';
    } else {
      newStrengthLevel = 1;
      newStrength = 'Weak';
      strengthColor = 'text-red-500';
    }
    
    setStrengthLevel(newStrengthLevel);
    setStrength(newStrength);
  };

  const generatePassword = () => {
    let chars = '';
    if (uppercase) chars += 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    if (lowercase) chars += 'abcdefghijklmnopqrstuvwxyz';
    if (numbers) chars += '0123456789';
    if (symbols) chars += '!@#$%^&*()_+[]{}|;:,.<>?';

    if (excludeSimilar) chars = chars.replace(similarChars, '');

    if (!chars) {
      alert('Please select at least one character type!');
      return;
    }

    let newPassword = '';
    const array = new Uint32Array(length);
    window.crypto.getRandomValues(array);
    
    for (let i = 0; i < length; i++) {
      newPassword += chars[array[i] % chars.length];
    }
    
    setPassword(newPassword);
    checkStrength();
    setCopied(false);
  };

  const copyPassword = () => {
    if (!password) return;
    
    navigator.clipboard.writeText(password).then(() => {
      setCopied(true);
      setTimeout(() => {
        setCopied(false);
      }, 2000);
    });
  };

  

  return (
    <>
    <HeaderSection toolId="randomPasswordGenerator" />
    <div className="max-w-4xl mx-auto p-4 lg:max-w-full lg:px-4">
      {/* Main container */}
      <div
       className="bg-white rounded-xl dark:bg-gray-800 shadow-sm dark:shadow-gray-900/10 border border-gray-200 dark:border-gray-700 overflow-hidden"
     
      >
         <div className="bg-gradient-to-r from-green-500 to-green-600 p-6 text-white">
                                <h1 className="text-2xl font-bold mb-2 flex items-center">
                                <FaLock className="text-white mr-3" />
                                    Random Password Generator
                                </h1>
                                <p className="opacity-90">
                                    Generate random, secure passwords with customizable options
                                </p>
                            </div>
        <div className="p-6 dark:text-white">
          {/* Header */}
          <h2 className="text-xl font-bold text-center mb-4 relative">
            <div className="flex items-center justify-center gap-2">
              <FaLock className="text-green-500" />
              <span>Secure Password Generator</span>
            </div>
            <div className="absolute bottom-[-8px] left-1/2 transform -translate-x-1/2 w-16 h-1 bg-gradient-to-r from-green-500 to-green-600 rounded-full"></div>
          </h2>

          {/* Password output */}
          <div
            className={`flex items-center justify-between p-4 rounded-xl mb-4 transition-colors ${
              darkMode ? 'bg-gray-700/50 border border-gray-600 hover:border-gray-500' : 'bg-green-100 border border-green-200 hover:border-green-300'
            }`}
          >
            <input
              type="text"
              value={password}
              readOnly
              placeholder="Your secure password"
              className={`w-full bg-transparent outline-none font-medium ${darkMode ? 'text-white placeholder-gray-400' : 'text-gray-800 dark:text-white dark:text-white dark:text-white dark:text-gray-100 dark:text-gray-100 placeholder-green-500'}`}
            />
            <button
              onClick={copyPassword}
              className={`ml-3 px-4 py-2 rounded-lg flex items-center gap-2 transition-all ${
                copied
                  ? 'bg-green-500 hover:bg-green-600'
                  : darkMode
                  ? 'bg-green-600 hover:bg-green-700'
                  : 'bg-green-500 hover:bg-green-600'
              } text-white font-semibold whitespace-nowrap`}
            >
              {copied ? <FaCheck /> : <FaCopy />}
              <span>{copied ? 'Copied!' : 'Copy'}</span>
            </button>
          </div>

          {/* Strength meter */}
          <div className="mb-6">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm font-medium">Password Strength:</span>
              <span
                className={`text-sm font-medium ${
                  strength === 'Strong'
                    ? 'text-green-500'
                    : strength === 'Good'
                    ? 'text-green-400'
                    : strength === 'Medium'
                    ? 'text-yellow-500'
                    : 'text-red-500'
                }`}
              >
                {strength}
              </span>
            </div>
            <div className="flex gap-2 h-2">
              {[1, 2, 3, 4].map((bar) => (
                <div
                  key={bar}
                  className={`flex-1 rounded-full transition-all ${
                    bar <= strengthLevel
                      ? strengthLevel >= 4
                        ? 'bg-green-500'
                        : strengthLevel >= 3
                        ? 'bg-green-400'
                        : strengthLevel >= 2
                        ? 'bg-yellow-500'
                        : 'bg-red-500'
                      : darkMode
                      ? 'bg-gray-600'
                      : 'bg-green-200'
                  }`}
                ></div>
              ))}
            </div>
          </div>

          {/* Controls */}
          <div className="mb-6">
            {/* Length slider */}
            <div className="mb-4">
              <label htmlFor="length" className="block text-sm font-medium mb-2 cursor-pointer">
                <div className="flex justify-between items-center">
                  <span>Password Length: <span className="font-semibold text-green-600">{length}</span></span>
                  <span className="text-xs text-green-500">6-32 characters</span>
                </div>
              </label>
              <div className="flex items-center gap-3">
                <input
                  type="range"
                  id="length"
                  min="6"
                  max="32"
                  value={length}
                  onChange={(e) => updateLengthValue(e.target.value)}
                  className={`w-full h-2 rounded-full appearance-none cursor-pointer ${
                    darkMode ? 'bg-gray-600' : 'bg-green-200'
                  }`}
                  style={{
                    backgroundImage: `linear-gradient(to right, #10b981 0%, #10b981 ${(length / 32) * 100}%, ${
                      darkMode ? '#374151' : '#d1fae5'
                    } ${(length / 32) * 100}%, ${darkMode ? '#374151' : '#d1fae5'} 100%)`,
                  }}
                />
                <span className="text-sm font-semibold min-w-[30px] text-center text-green-600">
                  {length}
                </span>
              </div>
            </div>

            {/* Checkbox options */}
            <div className="space-y-3">
              <label className="flex items-center justify-between p-2 rounded-lg cursor-pointer transition-colors hover:bg-green-100/30">
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={uppercase}
                    onChange={() => setUppercase(!uppercase)}
                    className={`w-5 h-5 rounded border-2 appearance-none checked:bg-green-500 checked:border-green-500 ${
                      darkMode ? 'border-gray-500' : 'border-green-400'
                    } relative`}
                  />
                  <span>Include Uppercase (A-Z)</span>
                </div>
              </label>

              <label className="flex items-center justify-between p-2 rounded-lg cursor-pointer transition-colors hover:bg-green-100/30">
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={lowercase}
                    onChange={() => setLowercase(!lowercase)}
                    className={`w-5 h-5 rounded border-2 appearance-none checked:bg-green-500 checked:border-green-500 ${
                      darkMode ? 'border-gray-500' : 'border-green-400'
                    } relative`}
                  />
                  <span>Include Lowercase (a-z)</span>
                </div>
              </label>

              <label className="flex items-center justify-between p-2 rounded-lg cursor-pointer transition-colors hover:bg-green-100/30">
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={numbers}
                    onChange={() => setNumbers(!numbers)}
                    className={`w-5 h-5 rounded border-2 appearance-none checked:bg-green-500 checked:border-green-500 ${
                      darkMode ? 'border-gray-500' : 'border-green-400'
                    } relative`}
                  />
                  <span>Include Numbers (0-9)</span>
                </div>
              </label>

              <label className="flex items-center justify-between p-2 rounded-lg cursor-pointer transition-colors hover:bg-green-100/30">
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={symbols}
                    onChange={() => setSymbols(!symbols)}
                    className={`w-5 h-5 rounded border-2 appearance-none checked:bg-green-500 checked:border-green-500 ${
                      darkMode ? 'border-gray-500' : 'border-green-400'
                    } relative`}
                  />
                  <span>Include Symbols (!@#$)</span>
                </div>
              </label>

              <label className="flex items-center justify-between p-2 rounded-lg cursor-pointer transition-colors hover:bg-green-100/30">
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={excludeSimilar}
                    onChange={() => setExcludeSimilar(!excludeSimilar)}
                    className={`w-5 h-5 rounded border-2 appearance-none checked:bg-green-500 checked:border-green-500 ${
                      darkMode ? 'border-gray-500' : 'border-green-400'
                    } relative`}
                  />
                  <span>Exclude Similar Characters (ilLI|`oO0)</span>
                </div>
              </label>
            </div>
          </div>

          {/* Generate button */}
          <button
            onClick={generatePassword}
            className="w-full py-3 px-6 bg-gradient-to-r from-green-500 to-green-600 text-white font-semibold rounded-xl flex items-center justify-center gap-3 transition-all duration-300 hover:from-green-600 hover:to-green-700 hover:shadow-lg dark:shadow-gray-900/30 dark:shadow-gray-900/30 dark:shadow-gray-900/30 dark:shadow-gray-900/30 dark:shadow-gray-900/30 hover:-translate-y-0.5 relative overflow-hidden"
          >
            <span className="relative z-10 flex items-center gap-2">
              <FaKey />
              Generate Password
            </span>
            <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent opacity-0 hover:opacity-100 transition-opacity duration-300 -translate-x-full group-hover:translate-x-full"></span>
          </button>
        </div>
      </div>

      {/* Custom styles */}
      <style data-jsx="true">{`
        .password-strength-bar {
          height: 4px;
          transition: all 0.3s ease;
        }
        .password-strength-bar.weak {
          background-color: #EF4444;
          width: 33%;
        }
        .password-strength-bar.medium {
          background-color: #F59E0B;
          width: 66%;
        }
        .password-strength-bar.strong {
          background-color: #10B981;
          width: 100%;
        }
      `}</style>
    </div>
    <BottomSection toolId="randomPasswordGenerator" />
    </>
  );
};

export default PasswordGenerator;